/**
 * `/api/drive-image` is only used in production on a deployed host (see shouldUseSameOriginDriveProxy).
 * `npm start` uses direct Drive URLs. To test the proxy locally: `netlify dev` or set
 * REACT_APP_DRIVE_IMAGE_PROXY_URL=https://your-deployed-site.com
 */
function isLocalDevHostname(hostname) {
  if (!hostname) return false;
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  return false;
}

/**
 * Same-origin proxy only for production builds on a real deploy (Netlify).
 * `npm start` is always NODE_ENV=development — use direct Drive URLs only (no /api on :3000).
 * `serve -s build` on localhost is production but hostname is local — still skip proxy.
 */
function shouldUseSameOriginDriveProxy(hostname) {
  if (process.env.NODE_ENV !== "production") return false;
  if (!hostname || isLocalDevHostname(hostname)) return false;
  return true;
}

function driveImageProxyUrlForFileId(id) {
  const explicit = (process.env.REACT_APP_DRIVE_IMAGE_PROXY_URL || "").replace(
    /\/$/,
    "",
  );
  const path = `/api/drive-image?id=${encodeURIComponent(id)}`;
  if (explicit) return `${explicit}${path}`;
  if (
    typeof window !== "undefined" &&
    shouldUseSameOriginDriveProxy(window.location.hostname)
  ) {
    return `${window.location.origin}${path}`;
  }
  return "";
}

const directDriveUrls = (id) => [
  `https://drive.google.com/uc?export=view&id=${id}`,
  `https://drive.google.com/uc?export=download&id=${id}`,
  `https://lh3.googleusercontent.com/d/${id}=w1000`,
  `https://drive.google.com/thumbnail?id=${id}&sz=w1000`,
  `https://lh3.googleusercontent.com/d/${id}`,
];

/**
 * Drive hotlinked URLs often work on desktop but fail on mobile (HTML / blocks).
 * On production we use only the same-origin proxy so the phone loads your domain.
 * The file must be shared as "Anyone with the link can view".
 */
export function driveImageCandidateUrls(raw) {
  if (!raw || typeof raw !== "string") return [];
  const s = raw.trim();
  if (!s || /^no\s*pic$/i.test(s)) return [];

  const lower = s.toLowerCase();
  if (
    lower.includes("drive.google.com") ||
    lower.includes("googleusercontent.com")
  ) {
    const id = extractGoogleDriveFileId(s);
    if (id) {
      const direct = directDriveUrls(id);
      const proxyUrl = driveImageProxyUrlForFileId(id);
      /* Proxy first on prod; if Netlify serves index.html for /api/* (misconfig), onError falls through to direct Drive URLs */
      if (proxyUrl) {
        return [proxyUrl, ...direct];
      }

      return direct;
    }
  }

  return [s];
}

/** @deprecated Prefer driveImageCandidateUrls for <img> (use fallbacks). */
export function imageUrlFromSheetPhotoLink(raw) {
  const urls = driveImageCandidateUrls(raw);
  return urls[0] || "";
}

export function extractGoogleDriveFileId(url) {
  if (!url || typeof url !== "string") return null;
  const openId = url.match(/[?&]id=([^&]+)/);
  if (openId) return openId[1];

  const dSlash = url.match(/\/d\/([^/]+)/);
  if (dSlash) return dSlash[1];

  return null;
}
